import React, { useState, useEffect, useMemo } from 'react';
import { ticketsAPI, exportAPI } from '../../lib/apiRoutes';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Search, Download, FileText, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const AdminHistorico = () => {
  const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
  const today = new Date().toISOString().split('T')[0];
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 100;
  
  // modoFecha: 'dia' = fecha exacta, 'rango' = from/to
  const [modoFecha, setModoFecha] = useState('rango');
  const [fechaExacta, setFechaExacta] = useState(today);
  
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    lugar: '',
    procedencia: '',
    fechaDesde: '',
    fechaHasta: '',
    soloConCoordenadas: false
  });

  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    // Set default to show last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFilters(prev => ({
      ...prev,
      fechaDesde: thirtyDaysAgo.toISOString().split('T')[0],
      fechaHasta: today.toISOString().split('T')[0]
    }));
  }, []);

  const loadTickets = async (newPage = 0) => {
    setLoading(true);
    try {
      const params = {
        skip: newPage * pageSize,
        limit: pageSize
      };
      
      if (appliedFilters.search) params.search = appliedFilters.search;
      if (appliedFilters.estado) params.estado = appliedFilters.estado;
      
      // Handle Cádiz group (Cádiz, Casco Antiguo, Puerta Tierra)
      if (appliedFilters.lugar === 'cadiz_grupo') {
        // Don't send lugar param - will filter on client side for multiple values
      } else if (appliedFilters.lugar) {
        params.lugar = appliedFilters.lugar;
      }
      
      if (appliedFilters.fechaDesde) params.fecha_desde = appliedFilters.fechaDesde;
      if (appliedFilters.fechaHasta) params.fecha_hasta = appliedFilters.fechaHasta;
      if (appliedFilters.soloConCoordenadas) params.solo_con_coordenadas = true;
      
      const response = await ticketsAPI.list(params);
      
      // Filter by procedencia and Cádiz group on client side
      let ticketsList = response.data.tickets || [];
      
      // Filter Cádiz group
      if (appliedFilters.lugar === 'cadiz_grupo') {
        const cadizZones = ['Cádiz', 'Casco Antiguo', 'Puerta Tierra'];
        ticketsList = ticketsList.filter(t => 
          cadizZones.some(zone => 
            t.zona?.toLowerCase().includes(zone.toLowerCase()) || 
            t.lugar?.toLowerCase().includes(zone.toLowerCase())
          )
        );
      }
      
      if (appliedFilters.procedencia) {
        ticketsList = ticketsList.filter(t => t.procedencia === appliedFilters.procedencia);
      }
      
      setTickets(ticketsList);
      setTotalCount(response.data.total || 0);
      setPage(newPage);
    } catch (error) {
      toast.error('Error cargando histórico');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const f = { ...filters };
    // Override date filters based on mode
    if (modoFecha === 'dia') {
      f.fechaDesde = fechaExacta;
      f.fechaHasta = fechaExacta;
    }
    setAppliedFilters(f);
    setPage(0);
  };

  const exportPDFDia = () => {
    const fecha = modoFecha === 'dia' ? fechaExacta : appliedFilters.fechaDesde;
    if (!fecha) { toast.error('Selecciona una fecha'); return; }
    window.open(`${API}/api/resumen-diario/export/pdf?fecha=${fecha}`, '_blank');
    toast.success(`Generando PDF para el ${fecha}...`);
  };

  const cambiarDia = (offset) => {
    const d = new Date(fechaExacta);
    d.setDate(d.getDate() + offset);
    setFechaExacta(d.toISOString().split('T')[0]);
  };

  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0) {
      loadTickets(0);
    }
  }, [appliedFilters]);

  const handleExport = () => {
    exportAPI.ticketsXLSX({
      fecha_desde: appliedFilters.fechaDesde,
      fecha_hasta: appliedFilters.fechaHasta,
      estado: appliedFilters.estado,
      lugar: appliedFilters.lugar
    });
    toast.success('Exportando a Excel...');
  };

  const clearFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFilters({
      search: '',
      estado: '',
      lugar: '',
      procedencia: '',
      fechaDesde: thirtyDaysAgo.toISOString().split('T')[0],
      fechaHasta: today.toISOString().split('T')[0],
      soloConCoordenadas: false
    });
  };

  const zonas = [
    { value: 'cadiz_grupo', label: 'Cádiz (todas: Cádiz, Casco Antiguo, Puerta Tierra)' },
    { value: 'Cádiz', label: 'Cádiz (solo)' },
    { value: 'Casco Antiguo', label: 'Casco Antiguo (solo)' },
    { value: 'Puerta Tierra', label: 'Puerta Tierra (solo)' },
    { value: 'Chiclana de la Frontera', label: 'Chiclana de la Frontera' },
    { value: 'Puerto Real', label: 'Puerto Real' },
    { value: 'San Fernando', label: 'San Fernando' },
    { value: 'Jerez de la Frontera', label: 'Jerez de la Frontera' },
    { value: 'El Puerto de Santa María', label: 'El Puerto de Santa María' }
  ];

  const estados = ['Pendiente', 'Notificada', 'Asignada', 'En proceso', 'Realizada', 'Cancelada', 'Comprobada', 'En espera'];
  
  const procedencias = ['web', 'whatsapp', 'ivr', 'manual', 'historico'];

  // Stats
  const stats = useMemo(() => {
    return {
      pendientes: tickets.filter(t => t.estado === 'Pendiente').length,
      realizadas: tickets.filter(t => t.estado === 'Realizada').length,
      canceladas: tickets.filter(t => t.estado === 'Cancelada').length
    };
  }, [tickets]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-5" data-testid="admin-historico-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-0.5">Consulta</p>
          <h1 className="text-2xl font-bold text-slate-900">Histórico de Recogidas</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {totalCount.toLocaleString('es-ES')} registros encontrados
          </p>
        </div>
        <div className="flex gap-2">
          {(modoFecha === 'dia' || appliedFilters.fechaDesde) && (
            <Button onClick={exportPDFDia} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="btn-export-pdf">
              <FileText className="w-4 h-4 mr-1.5" />
              PDF del día
            </Button>
          )}
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="btn-export-excel">
            <Download className="w-4 h-4 mr-1.5" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Search className="w-4 h-4" /> Filtros de Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Modo de fecha */}
            <div className="flex gap-2">
              <button
                onClick={() => setModoFecha('dia')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${modoFecha === 'dia' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="modo-dia"
              >
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Día exacto
              </button>
              <button
                onClick={() => setModoFecha('rango')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${modoFecha === 'rango' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="modo-rango"
              >
                Rango de fechas
              </button>
            </div>

            {/* Fecha exacta con nav */}
            {modoFecha === 'dia' && (
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl p-3">
                <Calendar className="w-4 h-4 text-teal-600" />
                <button onClick={() => cambiarDia(-1)} className="p-0.5 hover:bg-teal-100 rounded" data-testid="btn-dia-prev">
                  <ChevronLeft className="w-5 h-5 text-teal-700" />
                </button>
                <input
                  type="date"
                  value={fechaExacta}
                  onChange={(e) => setFechaExacta(e.target.value)}
                  className="border border-teal-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
                  data-testid="input-fecha-exacta"
                />
                <span className="text-sm text-teal-800 font-medium capitalize">
                  {new Date(fechaExacta + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
                <button onClick={() => cambiarDia(1)} className="p-0.5 hover:bg-teal-100 rounded" data-testid="btn-dia-next">
                  <ChevronRight className="w-5 h-5 text-teal-700" />
                </button>
                {fechaExacta !== today && (
                  <button onClick={() => setFechaExacta(today)} className="text-xs px-2 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700" data-testid="btn-hoy">
                    Hoy
                  </button>
                )}
              </div>
            )}

            {/* Row 1: Search and quick date buttons */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Buscar (ID, nombre, teléfono)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="CAD-000001, Juan Pérez, 666..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
              {modoFecha === 'rango' && (
                <div className="flex gap-2">
                  {[
                    { label: 'Hoy', fn: () => { const d = new Date(); setFilters(p => ({ ...p, fechaDesde: d.toISOString().split('T')[0], fechaHasta: d.toISOString().split('T')[0] })); } },
                    { label: '7 días', fn: () => { const d = new Date(); const a = new Date(d); a.setDate(d.getDate() - 7); setFilters(p => ({ ...p, fechaDesde: a.toISOString().split('T')[0], fechaHasta: d.toISOString().split('T')[0] })); } },
                    { label: '30 días', fn: () => { const d = new Date(); const a = new Date(d); a.setDate(d.getDate() - 30); setFilters(p => ({ ...p, fechaDesde: a.toISOString().split('T')[0], fechaHasta: d.toISOString().split('T')[0] })); } },
                    { label: '1 año', fn: () => { const d = new Date(); const a = new Date(d); a.setFullYear(d.getFullYear() - 1); setFilters(p => ({ ...p, fechaDesde: a.toISOString().split('T')[0], fechaHasta: d.toISOString().split('T')[0] })); } },
                    { label: 'Todo', fn: () => setFilters(p => ({ ...p, fechaDesde: '2020-01-01', fechaHasta: '2030-12-31' })) },
                  ].map(({ label, fn }) => (
                    <Button key={label} variant="outline" size="sm" onClick={fn}>{label}</Button>
                  ))}
                </div>
              )}
            </div>

            {/* Row 2: Date range + dropdowns */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {modoFecha === 'rango' && (
                <>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha Desde</Label>
                    <Input type="date" value={filters.fechaDesde} onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha Hasta</Label>
                    <Input type="date" value={filters.fechaHasta} onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })} />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs text-slate-500">Estado</Label>
                <select value={filters.estado} onChange={(e) => setFilters({ ...filters, estado: e.target.value })} className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Todos</option>
                  {estados.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Zona</Label>
                <select
                  value={filters.lugar}
                  onChange={(e) => setFilters({ ...filters, lugar: e.target.value })}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="">Todas</option>
                  {zonas.map(z => (
                    <option key={z.value} value={z.value}>{z.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Procedencia</Label>
                <select
                  value={filters.procedencia}
                  onChange={(e) => setFilters({ ...filters, procedencia: e.target.value })}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="">Todas</option>
                  {procedencias.map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Coordenadas</Label>
                <select
                  value={filters.soloConCoordenadas ? 'si' : ''}
                  onChange={(e) => setFilters({ ...filters, soloConCoordenadas: e.target.value === 'si' })}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="">Todos</option>
                  <option value="si">Solo con ubicación</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t">
              <div className="text-sm text-gray-500">
                Página {page + 1} de {totalPages || 1}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearFilters}>
                  Limpiar
                </Button>
                <Button onClick={applyFilters} disabled={loading}>
                  {loading ? 'Buscando...' : '🔍 Buscar'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">En esta página</p>
            <p className="text-2xl font-bold">{tickets.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50">
          <CardContent className="pt-4">
            <p className="text-sm text-orange-700">Pendientes</p>
            <p className="text-2xl font-bold text-orange-600">{stats.pendientes}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="pt-4">
            <p className="text-sm text-green-700">Realizadas</p>
            <p className="text-2xl font-bold text-green-600">{stats.realizadas}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-4">
            <p className="text-sm text-red-700">Canceladas</p>
            <p className="text-2xl font-bold text-red-600">{stats.canceladas}</p>
          </CardContent>
        </Card>
      </div>

      {/* Results Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">
              Resultados ({totalCount.toLocaleString()} total)
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                disabled={page === 0}
                onClick={() => loadTickets(page - 1)}
              >
                ← Anterior
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => loadTickets(page + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
              <p className="text-gray-600">Cargando datos...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-700">No hay resultados</h3>
              <p className="text-sm text-gray-500 mt-2">
                Ajusta los filtros y haz clic en "Buscar"
              </p>
              <Button onClick={applyFilters} className="mt-4">
                🔍 Buscar
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-2 font-semibold">Ticket ID</th>
                    <th className="text-left py-3 px-2 font-semibold">Ciudadano</th>
                    <th className="text-left py-3 px-2 font-semibold">Teléfono</th>
                    <th className="text-left py-3 px-2 font-semibold">Dirección</th>
                    <th className="text-left py-3 px-2 font-semibold">Zona</th>
                    <th className="text-left py-3 px-2 font-semibold">Estado</th>
                    <th className="text-left py-3 px-2 font-semibold">Fecha Recogida</th>
                    <th className="text-left py-3 px-2 font-semibold">Procedencia</th>
                    <th className="text-left py-3 px-2 font-semibold">Items</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket._id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 font-mono text-xs">{ticket.ticket_id}</td>
                      <td className="py-2 px-2">{ticket.nombre_ciudadano || '-'}</td>
                      <td className="py-2 px-2">{ticket.telefono || '-'}</td>
                      <td className="py-2 px-2 max-w-[200px]">
                        <div className="group relative">
                          <div className="truncate cursor-help">
                            {ticket.direccion || '-'}
                          </div>
                          {ticket.direccion && (
                            <div className="hidden group-hover:block absolute z-10 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-[300px] left-0 top-full mt-1">
                              <div className="font-semibold mb-2">📍 Dirección completa:</div>
                              <p className="mb-2">{ticket.direccion}</p>
                              {(ticket.bloque || ticket.piso) && (
                                <div className="text-gray-300 space-y-1">
                                  {ticket.bloque && <p>🏢 Bloque: {ticket.bloque}</p>}
                                  {ticket.piso && <p>🚪 Piso: {ticket.piso}</p>}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">{ticket.lugar || '-'}</td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ticket.estado === 'Realizada' ? 'bg-green-100 text-green-800' :
                          ticket.estado === 'Pendiente' ? 'bg-orange-100 text-orange-800' :
                          ticket.estado === 'Notificada' ? 'bg-purple-100 text-purple-800' :
                          ticket.estado === 'Asignada' ? 'bg-blue-100 text-blue-800' :
                          ticket.estado === 'Cancelada' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.estado}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {ticket.fecha_preferida_retirada 
                          ? new Date(ticket.fecha_preferida_retirada).toLocaleDateString('es-ES')
                          : '-'}
                      </td>
                      <td className="py-2 px-2">
                        <span className="px-2 py-1 rounded text-xs bg-gray-100">
                          {ticket.procedencia || 'web'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {ticket.items && ticket.items.length > 0 ? (
                          <div className="group relative">
                            <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 font-medium cursor-help">
                              {ticket.items.length} {ticket.items.length === 1 ? 'item' : 'items'}
                            </span>
                            <div className="hidden group-hover:block absolute z-10 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-[280px] left-0 top-full mt-1">
                              <div className="font-semibold mb-2">📦 Items para recoger:</div>
                              <ul className="space-y-2">
                                {ticket.items.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <span className="text-blue-400 font-bold">{idx + 1}.</span>
                                    <div>
                                      <span className="font-medium text-yellow-300">{item.tipo || item.tipo_enser || 'Item'}</span>
                                      {(item.descripcion || item.datos_item) && (
                                        <p className="text-gray-300 mt-0.5">{item.descripcion || item.datos_item}</p>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin items</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {tickets.length > 0 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline"
            disabled={page === 0}
            onClick={() => loadTickets(0)}
          >
            Primera
          </Button>
          <Button 
            variant="outline"
            disabled={page === 0}
            onClick={() => loadTickets(page - 1)}
          >
            ← Anterior
          </Button>
          <span className="px-4 py-2 bg-gray-100 rounded">
            Página {page + 1} de {totalPages}
          </span>
          <Button 
            variant="outline"
            disabled={page >= totalPages - 1}
            onClick={() => loadTickets(page + 1)}
          >
            Siguiente →
          </Button>
          <Button 
            variant="outline"
            disabled={page >= totalPages - 1}
            onClick={() => loadTickets(totalPages - 1)}
          >
            Última
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminHistorico;
